import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadPoolsByJobSector } from '../../../../../store/recruitmentService';
import { getNameFromId } from '../../../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../../utils/formValidation/commonConstants';
import { clearKeys } from '../../../../../utils/objectUtils';
import { stableSort } from '../../../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import SingleSelect from '../../../../formElements/SingleSelect';
import IconError from '../../../../IconError';
import ResultsTable from '../../../../table/ResultsTable';

import SelectPoolsRow from './SelectPoolsRow';

import classes from '../../submissionStyles/pools.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'ID' },
    { id: 'ownerName', numeric: false, label: 'Pool Owner' },
    { id: 'requirements', numeric: false, label: 'Pool Requirements', sortable: false },
    { id: 'select', numeric: false, label: 'Select', sortable: false }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'id',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const SelectPools = ({
    onRowsUpdated,
    poolApplications,
    jobSectorId,
    onUpdateJobSectorId,
    errors,
    clearError
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [keys, setKeys] = useState({ jobSectorId: '0' });
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const poolStatusDetails = useSelector(
        (state) => state.entities.directusService.poolStatusDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const selectedPools = useSelector((state) => state.entities.recruitmentService.pools);

    // USE EFFECTS
    useEffect(() => {
        setRows([]);
        setKeys(clearKeys(keys));
    }, []);

    useEffect(() => {
        if (!jobSectorId) {
            setRows([]);
        } else {
            dispatch(loadPoolsByJobSector(jobSectorId));
        }
    }, [jobSectorId]);

    useEffect(() => {
        if (selectedPools?.length < 1 || !jobSectorId) {
            setRows([]);
            return;
        }
        const poolsForJobSectorWithStatusId = selectedPools
            .map((el) => ({
                ...el,
                statusId:
                    poolApplications?.find((entry) => el.id === entry.participantPoolId)
                        ?.statusId || ''
            }))
            .filter(
                (el) =>
                    (el.teamIds.some((entry) => entry === currentParticipant.teamId) &&
                        el.statusId === '') ||
                    getNameFromId(poolStatusDetails, el.statusId).toLowerCase() === 'rejected'
            )
            .map((el) => {
                const user = users.find((entry) => entry.id === el?.ownerId);
                return {
                    ...el,
                    ownerName: `${user?.firstName || ''} ${user?.lastName || ''}`,
                    update: false
                };
            });
        setRows(poolsForJobSectorWithStatusId);
    }, [selectedPools, jobSectorId]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <SelectPoolsRow
                key={el.id}
                row={el}
                roles={roles}
                acceptedRoles={acceptedRoles}
                onRowChange={onRowChange}
            />
        ));
    };

    // EVENT HANDLERS
    const onJobSectorChange = (chosenId) => {
        if (errors.jobSectorId) clearError('jobSectorId');
        if (errors.participantPoolIds) clearError('participantPoolIds');
        if (!chosenId) chosenId = '';
        onUpdateJobSectorId(chosenId);
    };

    // EVENT HANDLER CALLBACKS
    const onRowChange = (row) => {
        const updatedRows = rows.map((el) => (el.id === row.id ? row : el));
        setRows(updatedRows);
        onRowsUpdated(updatedRows);
    };

    // RENDER
    return (
        <>
            <div className={classes.poolsToolBar}>
                <SingleSelect
                    id={'jobSectorId'}
                    key={keys.jobSectorId}
                    label={'Job Sector'}
                    placeholder="Select Job Sector..."
                    disabled={!hasRole(acceptedRoles, roles)}
                    mandatory={true}
                    menuItems={jobSectorDetails}
                    selectedId={jobSectorId}
                    selected={jobSectorDetails.find((el) => el.id === jobSectorId) || {}}
                    error={errors?.jobSectorId}
                    onChange={(chosenId) => onJobSectorChange(chosenId)}
                />
            </div>

            {rows.length < 1 ? (
                <div className={classes.noPools}>No pools found</div>
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
            <div className={classes.screenError}>
                {errors?.participantPoolIds?.error && (
                    <IconError text={errors.participantPoolIds} />
                )}
            </div>
        </>
    );
};

SelectPools.propTypes = {
    onRowsUpdated: PropTypes.func,
    poolApplications: PropTypes.array,
    jobSectorId: PropTypes.string,
    onUpdateJobSectorId: PropTypes.func,
    errors: PropTypes.object,
    clearError: PropTypes.func
};

export default SelectPools;
